import type { LiveReactionEmojiType } from "@/types/graphql/site.generated";
import type { LocalReaction } from "./batch";
import type { BurstLike } from "./subtractOwn";

/** Потолки из спеки: 30 значков в секунду, 8 облачков на экране. */
export const MAX_ICONS_PER_BURST = 30;
export const MAX_BUBBLES_ON_SCREEN = 8;

export type FlyingItem =
  | { key: string; kind: "emoji"; type: LiveReactionEmojiType; count: number }
  | { key: string; kind: "phrase"; index: number };

export function toFlyingItem(reaction: LocalReaction, key: string): FlyingItem {
  return reaction.kind === "emoji"
    ? { key, kind: "emoji", type: reaction.type, count: 1 }
    : { key, kind: "phrase", index: reaction.index };
}

/** Детерминированный «случайный» разброс по ключу: без Math.random в рендере. */
export function hashKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/**
 * Разворачивает пачку в отдельные значки. Если эмодзи больше 30, слоты
 * делятся по кругу между типами, и у типа, которому не хватило слотов,
 * последний значок несёт счётчик остатка («❤️ x37»): сумма по типу
 * сохраняется. Облачка фраз режутся по свободному месту на экране.
 */
export function expandBurst(
  burst: BurstLike,
  options: { activeBubbles: number; keyPrefix: string },
): FlyingItem[] {
  const items: FlyingItem[] = [];
  let sequence = 0;
  const nextKey = () => `${options.keyPrefix}-${sequence++}`;

  const emojis = burst.emojis.filter((emoji) => emoji.count > 0);
  const total = emojis.reduce((sum, emoji) => sum + emoji.count, 0);
  const slots = new Map<LiveReactionEmojiType, number>();

  if (total <= MAX_ICONS_PER_BURST) {
    emojis.forEach((emoji) => slots.set(emoji.type, emoji.count));
  } else {
    let left = MAX_ICONS_PER_BURST;
    while (left > 0) {
      let progressed = false;
      for (const emoji of emojis) {
        if (left === 0) break;
        const taken = slots.get(emoji.type) ?? 0;
        if (taken < emoji.count) {
          slots.set(emoji.type, taken + 1);
          left -= 1;
          progressed = true;
        }
      }
      if (!progressed) break;
    }
  }

  for (const emoji of emojis) {
    const slot = slots.get(emoji.type) ?? 0;
    if (slot >= emoji.count) {
      for (let i = 0; i < emoji.count; i++) {
        items.push({
          key: nextKey(),
          kind: "emoji",
          type: emoji.type,
          count: 1,
        });
      }
    } else if (slot > 0) {
      for (let i = 0; i < slot - 1; i++) {
        items.push({
          key: nextKey(),
          kind: "emoji",
          type: emoji.type,
          count: 1,
        });
      }
      items.push({
        key: nextKey(),
        kind: "emoji",
        type: emoji.type,
        count: emoji.count - (slot - 1),
      });
    }
  }

  let bubbles = Math.max(0, MAX_BUBBLES_ON_SCREEN - options.activeBubbles);
  for (const phrase of burst.phrases) {
    for (let i = 0; i < phrase.count && bubbles > 0; i++) {
      items.push({ key: nextKey(), kind: "phrase", index: phrase.index });
      bubbles -= 1;
    }
  }

  return items;
}
